import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, RefreshCw, ShieldAlert } from "lucide-react";
import { api, assetUrl, errorCode, formatApiErrorDetail } from "../lib/api";
import { formatMoney, formatCents } from "../lib/pricing";
import { dateToLongLabel, prettyTime } from "../lib/time";
import { clearWizard, loadWizard } from "../lib/wizardState";

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "true";
  const [state] = useState(() => loadWizard());
  const [yacht, setYacht] = useState(null);
  const [settings, setSettings] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const complete =
    state.slug && state.date && state.time && state.duration &&
    state.customer_name && state.customer_email && state.customer_phone &&
    state.guest_count && state.start_datetime_utc;

  useEffect(() => {
    if (!state.slug) return;
    api.get(`/yachts/${state.slug}`).then((r) => setYacht(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, [state.slug]);

  useEffect(() => {
    if (!complete || !yacht) return;
    let ignore = false;
    setQuoteErr("");
    api.post("/bookings/quote", {
      yacht_id: yacht.id,
      start_datetime: state.start_datetime_utc,
      duration_hours: state.duration,
      guest_count: state.guest_count,
    })
      .then((r) => !ignore && setQuote(r.data))
      .catch((e) => !ignore && setQuoteErr(formatApiErrorDetail(e)));
    return () => { ignore = true; };
  }, [complete, yacht, state.duration, state.guest_count, state.start_datetime_utc]);

  const breakdown = quote?.breakdown;
  const dep = quote?.deposit;
  const depositAmount = dep ? dep.amount_cents : (settings?.deposit_amount_cents ?? 50000);
  const totalCents = dep?.total_cents ?? 0;
  const balanceDueCents = dep?.balance_due_cents ?? 0;

  async function goToPayment() {
    if (!yacht || !complete) return;
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/checkout", {
        yacht_id: yacht.id,
        start_datetime: state.start_datetime_utc,
        duration_hours: state.duration,
        guest_count: state.guest_count,
        customer_name: state.customer_name,
        customer_email: state.customer_email,
        customer_phone: state.customer_phone,
        special_request: state.special_request || null,
        occasion: state.occasion || null,
        origin_url: window.location.origin,
      });
      const url = res.data?.checkout_url;
      if (!url) throw new Error("Missing checkout URL");
      clearWizard();
      window.location.href = url;
    } catch (err) {
      setSubmitting(false);
      const code = errorCode(err);
      if (code === "SLOT_JUST_TAKEN" || code === "OVERLAP_WITH_EXISTING_BOOKING" || code === "OVERLAP_WITH_ADMIN_BLOCK") {
        toast.error("That time was just booked. Please pick another time.");
      } else if (code === "LEAD_TIME_VIOLATION") {
        toast.error("This slot is too close to now — please pick a start time at least 24 hours away.");
      } else {
        toast.error(formatApiErrorDetail(err));
      }
    }
  }

  if (!complete) {
    return (
      <div className="hbe-container py-20" data-testid="checkout-incomplete">
        <h1 className="font-serif text-3xl">Your booking is incomplete</h1>
        <p className="mt-3 text-hbe-ink/70">
          Please complete the booking wizard first.
        </p>
        <Link to="/yachts" className="hbe-btn-primary mt-6 inline-flex">
          Choose a yacht
        </Link>
      </div>
    );
  }

  return (
    <div className="hbe-container py-10 md:py-16" data-testid="checkout-page">
      <Link
        to={`/book/${state.slug}`}
        className="text-sm text-hbe-ink/70 hover:text-hbe-primary"
        data-testid="checkout-back"
      >
        ← Back to booking
      </Link>

      <h1 className="mt-4 font-sans font-semibold text-4xl md:text-5xl">Review your reservation</h1>
      <p className="mt-2 text-hbe-ink/70 max-w-xl">
        Lock your yacht with a {formatCents(depositAmount)} deposit today. Pay the balance on your charter day.
      </p>

      {cancelled && (
        <div
          className="mt-6 border border-amber-300 bg-amber-50 text-amber-900 rounded-lg p-4 flex items-start gap-3"
          data-testid="checkout-cancelled-banner"
        >
          <RefreshCw size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Payment cancelled</div>
            <div className="text-sm mt-0.5">
              No charges were made. Your reservation details are still here —
              tap "Pay {formatCents(depositAmount)} deposit" to try again.
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_400px]">
        {/* Summary */}
        <div className="space-y-6" data-testid="checkout-summary">
          <div className="hbe-card overflow-hidden">
            {yacht?.images?.[0] && (
              <img
                src={assetUrl(yacht.images[0])}
                alt={`${yacht.name} — reservation summary`}
                className="w-full aspect-[16/8] object-cover"
                loading="lazy"
              />
            )}
            <div className="p-6">
              <div className="hbe-eyebrow">Miami · Private charter</div>
              <h2 className="mt-2 font-sans font-semibold text-2xl">{yacht?.name || "Yacht"}</h2>
            </div>
          </div>

          <div className="hbe-card p-6 space-y-4 text-sm">
            <Line label="Date" value={dateToLongLabel(state.date)} testid="sum-date" />
            <Line label="Start time (ET)" value={prettyTime(state.time)} testid="sum-time" />
            <Line label="Duration" value={`${state.duration} hours`} testid="sum-duration" />
            <Line label="Guests" value={String(state.guest_count)} testid="sum-guests" />
            <Line label="Name" value={state.customer_name} testid="sum-name" />
            <Line label="Email" value={state.customer_email} testid="sum-email" />
            <Line label="Phone" value={state.customer_phone} testid="sum-phone" />
            {state.special_request && (
              <Line label="Notes" value={state.special_request} testid="sum-request" />
            )}
            {state.occasion && (
              <Line label="Occasion" value={state.occasion} testid="sum-occasion" />
            )}
          </div>
        </div>

        {/* Price + Reserve */}
        <aside className="space-y-6">
          <div className="hbe-card p-6" data-testid="checkout-price">
            <div className="hbe-eyebrow">Price</div>
            {quoteErr && (
              <div className="mt-4 text-sm text-red-700" data-testid="quote-error">
                {quoteErr}
              </div>
            )}
            {!breakdown && !quoteErr && (
              <div className="mt-4 text-sm text-hbe-ink/55">Calculating…</div>
            )}
            {breakdown && (
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-hbe-ink/70">
                    Base ({breakdown.base_covers_hours} hours)
                  </span>
                  <span>{formatMoney(breakdown.base_price)}</span>
                </div>
                {breakdown.additional_hours > 0 && (
                  <div className="flex justify-between">
                    <span className="text-hbe-ink/70">
                      {breakdown.additional_hours} additional hour
                      {breakdown.additional_hours > 1 ? "s" : ""}
                    </span>
                    <span>{formatMoney(breakdown.additional_hours_subtotal)}</span>
                  </div>
                )}
                {settings?.tax_enabled && breakdown.tax_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-hbe-ink/70">
                      {breakdown.tax_label} ({breakdown.tax_rate_percent}%)
                    </span>
                    <span>{formatMoney(breakdown.tax_amount)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-hbe-line flex justify-between text-hbe-ink/75">
                  <span>Charter total</span>
                  <span data-testid="price-total">{formatMoney(breakdown.total)}</span>
                </div>
                <div className="flex justify-between items-baseline bg-hbe-primary/[.04] rounded-md -mx-2 px-2 py-2">
                  <span className="font-medium text-hbe-primary">Due now (deposit)</span>
                  <span
                    className="font-sans font-semibold text-2xl text-hbe-primary"
                    data-testid="price-deposit"
                  >
                    {formatCents(depositAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-hbe-ink/70">
                  <span>Balance due on charter day</span>
                  <span data-testid="price-balance-due">{formatCents(balanceDueCents)}</span>
                </div>
              </div>
            )}
            {breakdown && (
              <div
                className="mt-4 space-y-1"
                data-testid="deposit-policy-blurb"
              >
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#557A95", fontWeight: 400 }}
                >
                  Your charter total is {formatCents(breakdown.total_cents)}.
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#557A95", fontWeight: 400 }}
                >
                  You pay {formatCents(depositAmount)} today as a{" "}
                  <span className="font-semibold">non-refundable deposit</span>{" "}
                  — this locks your yacht.
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "#557A95", fontWeight: 400 }}
                >
                  The remaining {formatCents(balanceDueCents)} is due on the
                  day of your charter (cash or another arrangement with our
                  team).
                </p>
                <p
                  className="mt-2 text-base leading-relaxed"
                  style={{ color: "#062843", fontWeight: 600 }}
                  data-testid="deposit-math-line"
                >
                  {formatCents(depositAmount)} today +{" "}
                  {formatCents(balanceDueCents)} on charter day ={" "}
                  {formatCents(breakdown.total_cents)} total.
                </p>
              </div>
            )}
          </div>

          <div className="hbe-card p-6" data-testid="checkout-payment">
            <div className="hbe-eyebrow">Payment</div>
            <div className="mt-3 flex items-start gap-2 text-sm text-hbe-ink/75">
              <Lock size={16} className="mt-0.5 text-hbe-accent shrink-0" />
              <p>
                You'll pay the deposit securely on Stripe. Card details never
                touch our servers.
              </p>
            </div>
            <button
              type="button"
              onClick={goToPayment}
              disabled={submitting || !breakdown}
              className="hbe-btn-primary w-full mt-5"
              data-testid="continue-to-payment-btn"
            >
              {submitting
                ? "Redirecting…"
                : `Pay ${formatCents(depositAmount)} deposit — non-refundable`}
            </button>
            <p className="mt-3 text-[11px] text-hbe-ink/50 text-center flex items-center justify-center gap-1">
              <ShieldAlert size={12} /> Deposit is non-refundable. Balance paid on charter day.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Line({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 border-b border-hbe-line/60 pb-3 last:border-0 last:pb-0">
      <div className="text-hbe-ink/55">{label}</div>
      <div className="text-right text-hbe-ink" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
